/**
 * DIFFICULTY MODEL — and why raw reaction-time scaling is not enough
 * ==================================================================
 *
 * The original proposal was to scale difficulty purely by shrinking the
 * opponent's reaction delay: 700 ms down to 80 ms. The instinct is right — it is
 * far better than inflating damage or health, because it changes *what the player
 * must do* rather than how long they must do it for. Keep that instinct.
 *
 * But the specific numbers break in two places.
 *
 * 1. THE FLOOR IS BELOW HUMAN. Simple visual reaction time is ~200-250 ms;
 *    elite athletes reach ~150 ms on a *prepared, single-choice* stimulus.
 *    Choice reaction (which of six punches is this?) is slower still. An
 *    opponent at 80 ms is not "professional", it is precognitive. Players do not
 *    perceive sub-human latency as skill — they perceive it as cheating, and they
 *    stop trying to outthink the AI because outthinking it visibly does not work.
 *    That is the exact failure mode that kills a skill trainer.
 *
 * 2. REACTION TIME ALONE IS ONE-DIMENSIONAL. Two fighters with identical latency
 *    but different anticipation, output and discipline feel completely different.
 *    A ladder that only moves latency produces six versions of the same fight.
 *
 * THE FIX: keep reaction time as the *spine*, floor it at ~170 ms, and get the
 * rest of the range from ANTICIPATION. A high-skill boxer does not react faster
 * than physics allows; they start moving earlier because they read the punch
 * before it lands. We model that explicitly with an online predictor (see
 * blackboard.ts). Anticipation lets the AI act on a *predicted* event, which
 * looks like a 60 ms reaction while remaining causally honest — and, crucially,
 * it is BEATABLE: break your patterns and the prediction fails. Reaction time is
 * not beatable, which is why leaning on it alone feels unfair.
 *
 * Six other axes move with the ladder so higher tiers are qualitatively, not just
 * quantitatively, harder. Mistake rate never reaches zero: an opponent that never
 * errs gives the player nothing to punish, and punishing errors is most of the
 * satisfaction in sparring.
 */

export interface DifficultyProfile {
  id: string;
  name: string;
  /** Perception lag on player events, ms. Floored at a humanly plausible value. */
  reactionMs: number;
  /** Random jitter added to reaction, ms. Zero jitter reads as robotic. */
  reactionJitterMs: number;
  /**
   * 0..1. Probability the AI acts on a *predicted* next player action instead of
   * waiting to observe it. This is the real difficulty dial above "Medium".
   */
  anticipation: number;
  /** Multiplier on punch output rate. */
  outputRate: number;
  /** 0..1. How much of the defensive repertoire it actually uses. */
  defensiveCoverage: number;
  /** 0..1. Probability of a self-inflicted error per exchange (dropping hands, over-committing). */
  mistakeRate: number;
  /** 0..1. How often it falls for a player feint. High = exploitable. */
  feintSusceptibility: number;
  /** 0..1. How well it holds its preferred range. */
  positioning: number;
  /** Stamina pool multiplier. */
  conditioning: number;

  // --- Punch-Out mode -------------------------------------------------------
  // At fixed range the difficulty question changes from "how fast does it react
  // to me?" to "how much warning do I get, and how much am I rewarded for
  // taking it?". These are the axes that actually gate the player's skill here.

  /** Multiplier on the opponent's wind-up. >1 = a long, generous tell. */
  telegraphScale: number;
  /** Multiplier on the punish window earned by a good dodge. */
  windowScale: number;
  /** Multiplier on how often it attacks. */
  pressure: number;
  /**
   * Show the player which way to move during the wind-up. A training wheel, and
   * only honest at the bottom of the ladder — above Easy, reading the tell IS
   * the skill and prompting for it removes the game.
   */
  showDodgePrompt: boolean;
}

/**
 * NOTE ON THE NUMBERS: `reactionMs` here is the *decision* latency, applied on
 * top of the ~30-60 ms the pose pipeline already costs. So the player-perceived
 * total is roughly reactionMs + 45 ms. Beginner therefore feels like ~745 ms,
 * which is correct — it should feel almost comically slow, because a beginner
 * needs to see their own punches land before they can learn anything.
 */
export const DIFFICULTIES: DifficultyProfile[] = [
  {
    id: 'beginner',
    name: 'Beginner',
    reactionMs: 700,
    reactionJitterMs: 120,
    anticipation: 0.0,
    outputRate: 0.45,
    defensiveCoverage: 0.25,
    mistakeRate: 0.45,
    feintSusceptibility: 0.85,
    positioning: 0.3,
    telegraphScale: 2.10,
    windowScale: 1.60,
    pressure: 0.55,
    showDodgePrompt: true,
    conditioning: 0.7,
  },
  {
    id: 'easy',
    name: 'Easy',
    reactionMs: 560,
    reactionJitterMs: 100,
    anticipation: 0.05,
    outputRate: 0.6,
    defensiveCoverage: 0.4,
    mistakeRate: 0.35,
    feintSusceptibility: 0.7,
    positioning: 0.45,
    telegraphScale: 1.70,
    windowScale: 1.35,
    pressure: 0.70,
    showDodgePrompt: true,
    conditioning: 0.8,
  },
  {
    id: 'medium',
    name: 'Medium',
    reactionMs: 420,
    reactionJitterMs: 80,
    anticipation: 0.15,
    outputRate: 0.8,
    defensiveCoverage: 0.6,
    mistakeRate: 0.25,
    feintSusceptibility: 0.55,
    positioning: 0.6,
    telegraphScale: 1.30,
    windowScale: 1.10,
    pressure: 0.90,
    showDodgePrompt: false,
    conditioning: 0.9,
  },
  {
    id: 'hard',
    name: 'Hard',
    reactionMs: 300,
    reactionJitterMs: 55,
    anticipation: 0.3,
    outputRate: 1.0,
    defensiveCoverage: 0.75,
    mistakeRate: 0.16,
    feintSusceptibility: 0.4,
    positioning: 0.75,
    telegraphScale: 1.00,
    windowScale: 1.00,
    pressure: 1.10,
    showDodgePrompt: false,
    conditioning: 1.0,
  },
  {
    id: 'elite',
    name: 'Elite',
    // Floored here, not at 150. Below ~200 ms a human cannot *initiate* a
    // response to an unexpected stimulus; the remaining difficulty comes from
    // reading you, which is what `anticipation` buys.
    reactionMs: 220,
    reactionJitterMs: 35,
    anticipation: 0.5,
    outputRate: 1.15,
    defensiveCoverage: 0.88,
    mistakeRate: 0.09,
    feintSusceptibility: 0.28,
    positioning: 0.88,
    telegraphScale: 0.80,
    windowScale: 0.85,
    pressure: 1.30,
    showDodgePrompt: false,
    conditioning: 1.1,
  },
  {
    id: 'professional',
    name: 'Professional',
    // The hard floor. Everything past this point is anticipation and discipline.
    reactionMs: 170,
    reactionJitterMs: 25,
    anticipation: 0.72,
    outputRate: 1.3,
    defensiveCoverage: 0.95,
    // Never zero. An opponent with no flaws is an opponent with no lesson.
    mistakeRate: 0.05,
    feintSusceptibility: 0.18,
    positioning: 0.95,
    telegraphScale: 0.62,
    windowScale: 0.72,
    pressure: 1.50,
    showDodgePrompt: false,
    conditioning: 1.2,
  },
];

export function difficultyById(id: string): DifficultyProfile {
  return DIFFICULTIES.find((d) => d.id === id) ?? DIFFICULTIES[2];
}

/**
 * DYNAMIC DIFFICULTY ADJUSTMENT
 *
 * Fixed tiers are the wrong granularity for a fitness/skill app, because the same
 * player is sharp on Tuesday and dead on Thursday. This nudges the *effective*
 * profile toward a target performance band rather than switching tiers, which
 * keeps the opponent's identity stable while keeping the fight competitive.
 *
 * It is deliberately slow (adjusts over ~15 s) and bounded (+/-1 tier of effect).
 * Rubber-banding that a player can *feel* is worse than a fight that is slightly
 * too hard, because it invalidates their sense of progress.
 */
export class AdaptiveDifficulty {
  /** -1 = ease off, +1 = press. */
  private bias = 0;
  /** Exponentially-weighted "how is the player doing", -1..1. */
  private form = 0;

  constructor(
    private enabled = true,
    /** Target: player lands slightly more than they eat. */
    private targetDifferential = 0.12,
  ) {}

  /** Feed once per exchange. `differential` = (player quality - ai quality) in -1..1. */
  observe(differential: number, dtMs: number): void {
    if (!this.enabled) return;
    const k = 1 - Math.pow(0.5, dtMs / 4000);
    this.form += (differential - this.form) * k;
    const error = this.form - this.targetDifferential;
    const kb = 1 - Math.pow(0.5, dtMs / 15000);
    this.bias += (Math.max(-1, Math.min(1, error * 2.2)) - this.bias) * kb;
  }

  apply(base: DifficultyProfile): DifficultyProfile {
    if (!this.enabled || Math.abs(this.bias) < 0.02) return base;
    const b = this.bias;
    const lerp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    return {
      ...base,
      // Reaction moves least — it is the opponent's "identity" and players notice
      // it changing. Output and positioning carry most of the adaptation.
      reactionMs: lerp(base.reactionMs * (1 - 0.18 * b), 170, 900),
      anticipation: lerp(base.anticipation + 0.12 * b, 0, 0.85),
      outputRate: lerp(base.outputRate * (1 + 0.3 * b), 0.3, 1.6),
      defensiveCoverage: lerp(base.defensiveCoverage + 0.12 * b, 0.15, 0.98),
      mistakeRate: lerp(base.mistakeRate * (1 - 0.35 * b), 0.04, 0.6),
      positioning: lerp(base.positioning + 0.1 * b, 0.2, 0.98),
    };
  }

  get pressure(): number {
    return this.enabled ? this.bias : 0;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.reset();
  }

  reset(): void {
    this.bias = 0;
    this.form = 0;
  }
}
